import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Form, Row, Col, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Edit,
  Copy,
  Archive,
  Search,
  Filter,
  ChevronDown,
  FileText,
  MessageSquare,
  Mail,
  Video,
  Clock,
  CheckCircle,
  AlertCircle,
} from "react-feather";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./TemplateList.module.scss";

// Dummy data
const DUMMY_TEMPLATES = [
  {
    id: "1",
    name: "Meeting Reminder",
    scope: "Company",
    language: "English",
    basedOnGlobalName: "Standard Reminder",
    version: "v2.1.0",
    status: "active",
    channel: "teams",
    type: "Reminder",
    hasOverride: false,
    lastModified: "2024-01-15",
    usageCount: 1247,
  },
  {
    id: "2",
    name: "MOM Summary",
    scope: "Department",
    language: "English",
    basedOnGlobalName: "Meeting Minutes",
    version: "v1.3.0",
    status: "active",
    channel: "whatsapp",
    type: "MOM",
    hasOverride: true,
    lastModified: "2024-01-10",
    usageCount: 892,
  },
  {
    id: "3",
    name: "Booking Confirmation",
    scope: "Company",
    language: "Spanish",
    basedOnGlobalName: "Booking Template",
    version: "v3.0.1",
    status: "draft",
    channel: "email",
    type: "Booking",
    hasOverride: false,
    lastModified: "2024-01-18",
    usageCount: 0,
  },
  {
    id: "4",
    name: "Follow-up Message",
    scope: "Team",
    language: "French",
    basedOnGlobalName: "Standard Follow-up",
    version: "v1.0.0",
    status: "archived",
    channel: "teams",
    type: "Reminder",
    hasOverride: false,
    lastModified: "2023-12-20",
    usageCount: 345,
  },
  {
    id: "5",
    name: "Weekly Digest",
    scope: "Company",
    language: "English",
    basedOnGlobalName: "Digest Template",
    version: "v2.2.0",
    status: "active",
    channel: "whatsapp",
    type: "Reminder",
    hasOverride: true,
    lastModified: "2024-01-12",
    usageCount: 2156,
  },
  {
    id: "6",
    name: "Meeting Invite",
    scope: "Department",
    language: "German",
    basedOnGlobalName: "Invite Template",
    version: "v1.5.0",
    status: "active",
    channel: "email",
    type: "Booking",
    hasOverride: false,
    lastModified: "2024-01-08",
    usageCount: 567,
  },
];

const getChannelIcon = (channel) => {
  switch (channel) {
    case "teams":
      return <Video size={16} />;
    case "whatsapp":
      return <MessageSquare size={16} />;
    case "email":
      return <Mail size={16} />;
    default:
      return <FileText size={16} />;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "active":
      return <CheckCircle size={14} />;
    case "draft":
      return <FileText size={14} />;
    case "archived":
      return <Archive size={14} />;
    default:
      return <AlertCircle size={14} />;
  }
};

const getStatusClass = (status) => {
  switch (status) {
    case "active":
      return styles.statusActive;
    case "draft":
      return styles.statusDraft;
    case "archived":
      return styles.statusArchived;
    default:
      return "";
  }
};

export default function TemplateList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [channel, setChannel] = useState("all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));

    let filtered = [...DUMMY_TEMPLATES];

    if (channel !== "all") {
      filtered = filtered.filter((t) => t.channel === channel);
    }
    if (type !== "all") {
      filtered = filtered.filter((t) => t.type === type);
    }
    if (status !== "all") {
      filtered = filtered.filter((t) => t.status === status);
    }

    setRows(filtered);
    setFilteredRows(filtered);
    setLoading(false);
  }, [channel, type, status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let filtered = [...rows];

    if (searchTerm) {
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.scope.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.language.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredRows(filtered);
  }, [searchTerm, rows]);

  const handleArchive = (id, name) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    toast.success(`${name} archived successfully`);
  };

  const breadcrumbs = [
    { label: "Company", path: companyUrl("dashboard") },
    { label: "Templates" },
  ];

  if (loading && !rows.length) {
    return (
      <div>
        <PageHeader title="Templates" breadcrumbs={breadcrumbs} />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <PageHeader
        title="Templates"
        breadcrumbs={breadcrumbs}
        primaryAction={{
          label: "New Template",
          onClick: () => navigate(companyUrl("templates/new")),
          className: styles.btnPrimary,
        }}
      />

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <FileText />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Total Templates</span>
                <span className={styles.statValue}>
                  {DUMMY_TEMPLATES.length}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <CheckCircle />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Active</span>
                <span className={styles.statValue}>
                  {DUMMY_TEMPLATES.filter((t) => t.status === "active").length}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <Clock />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Draft</span>
                <span className={styles.statValue}>
                  {DUMMY_TEMPLATES.filter((t) => t.status === "draft").length}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <Archive />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Archived</span>
                <span className={styles.statValue}>
                  {
                    DUMMY_TEMPLATES.filter((t) => t.status === "archived")
                      .length
                  }
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters Section */}
      <Card className={styles.filtersCard}>
        <Card.Body>
          <div
            className={styles.filtersHeader}
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className={styles.filtersTitle}>
              <Filter size={18} />
              <span>Filters</span>
            </div>
            <ChevronDown
              size={18}
              className={showFilters ? styles.chevronRotated : ""}
            />
          </div>

          {showFilters && (
            <div className={styles.filtersContent}>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label className={styles.filterLabel}>
                    Channel
                  </Form.Label>
                  <Form.Select
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    className={styles.selectInput}
                  >
                    <option value="all">All Channels</option>
                    <option value="teams">Microsoft Teams</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label className={styles.filterLabel}>
                    Template Type
                  </Form.Label>
                  <Form.Select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className={styles.selectInput}
                  >
                    <option value="all">All Types</option>
                    <option value="Reminder">Reminder</option>
                    <option value="MOM">Meeting Minutes</option>
                    <option value="Booking">Booking Confirmation</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label className={styles.filterLabel}>Status</Form.Label>
                  <Form.Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={styles.selectInput}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Search Bar */}
      <div className={styles.searchSection}>
        <div className={styles.searchWrapper}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search templates by name, scope, or language..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.resultCount}>
          Found {filteredRows.length} template
          {filteredRows.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Templates Grid */}
      <Row className="g-4">
        {filteredRows.map((template) => (
          <Col lg={6} xl={4} key={template.id}>
            <Card className={styles.templateCard}>
              <Card.Body>
                {/* Card Header */}
                <div className={styles.cardHeader}>
                  <div className={styles.channelBadge}>
                    {getChannelIcon(template.channel)}
                    <span>
                      {template.channel.charAt(0).toUpperCase() +
                        template.channel.slice(1)}
                    </span>
                  </div>
                  <div
                    className={`${styles.statusBadge} ${getStatusClass(template.status)}`}
                  >
                    {getStatusIcon(template.status)}
                    <span>{template.status}</span>
                  </div>
                </div>

                {/* Template Name */}
                <div className={styles.templateName}>
                  <h3>{template.name}</h3>
                  {template.hasOverride && (
                    <Badge className={styles.overrideBadge}>Override</Badge>
                  )}
                </div>

                {/* Template Meta */}
                <div className={styles.templateMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Scope</span>
                    <span className={styles.metaValue}>{template.scope}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Language</span>
                    <span className={styles.metaValue}>
                      {template.language}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Version</span>
                    <span className={styles.metaValue}>{template.version}</span>
                  </div>
                </div>

                {/* Based On */}
                <div className={styles.basedOn}>
                  <span>Based on: </span>
                  <strong>{template.basedOnGlobalName}</strong>
                </div>

                {/* Stats Footer */}
                <div className={styles.cardFooter}>
                  <div className={styles.usageStats}>
                    <span>
                      Used {template.usageCount.toLocaleString()} times
                    </span>
                    <span>
                      Updated{" "}
                      {new Date(template.lastModified).toLocaleDateString()}
                    </span>
                  </div>

                  <div className={styles.cardActions}>
                    <button
                      className={styles.actionBtn}
                      onClick={() =>
                        navigate(companyUrl(`templates/${template.id}`))
                      }
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className={styles.actionBtn}
                      onClick={() =>
                        navigate(
                          companyUrl(`templates/new?from=${template.id}`),
                        )
                      }
                      title="Create Override"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => handleArchive(template.id, template.name)}
                      title="Archive"
                    >
                      <Archive size={16} />
                    </button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {filteredRows.length === 0 && !loading && (
        <div className={styles.emptyState}>
          <FileText size={48} />
          <h4>No templates found</h4>
          <p>Try adjusting your filters or create a new template</p>
          <Button
            className={styles.btnPrimary}
            onClick={() => navigate(companyUrl("templates/new"))}
          >
            Create New Template
          </Button>
        </div>
      )}
    </div>
  );
}
